// Staged handouts (issue 1154): the "Handouts" tool. The DM's list — write or edit a handout (title, plain text, up to
// six pictures from the Library), Send to… everyone or chosen players, Take back, Delete, and pin it on the map or pin a
// note of their own; a pin lands at the centre of the DM's view. A player's list is what they were given, newest first,
// each opened to read. The text is drawn as text, never as markup; a picture is fetched with the bearer like any other.
import { useEffect, useState } from 'react';
import { HANDOUT_LIMITS, memberLabel, type AssetInfo, type Handout, type Pin, type PlayerHandout } from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { useShell } from '../context';
import { listAssets, pictureUrl } from '../../assets';
import { boardBus } from '../../board/bus';
import { audienceWords, handoutList, paragraphs } from '../../handouts';
import { BTN } from '../ui';

const btn = `${BTN} shrink-0`;
const input = 'w-full rounded border border-stone-700 bg-stone-900 px-2 py-1 text-sm';

/** One picture, fetched with the bearer; nothing while it loads or when it may not be read. */
export function HandoutPicture({ id, alt }: { id: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => { let live = true; void pictureUrl(id).then((u) => { if (live) setUrl(u); }); return () => { live = false; }; }, [id]);
  return url ? <img src={url} alt={alt} className="max-h-64 max-w-full rounded" data-testid="handout-picture" /> : null;
}

/** A handout as it reads: its title, its paragraphs as text, its pictures. */
export function HandoutView({ handout }: { handout: Handout | PlayerHandout }) {
  return (
    <article className="space-y-2" data-testid="handout-view">
      <h3 className="vtt-title">{handout.title}</h3>
      {paragraphs(handout.body).map((p, i) => <p key={i} className="whitespace-pre-wrap break-words">{p}</p>)}
      {handout.pictures.map((id, i) => <HandoutPicture key={id} id={id} alt={`${handout.title}, picture ${i + 1}`} />)}
    </article>
  );
}

const newId = () => crypto.randomUUID();

function HandoutForm({ start, onDone }: { start: Handout | null; onDone: () => void }) {
  const { act, setNotice } = useTableState();
  const [title, setTitle] = useState(start?.title ?? '');
  const [body, setBody] = useState(start?.body ?? '');
  const [pictures, setPictures] = useState<string[]>(start?.pictures ?? []);
  const [library, setLibrary] = useState<AssetInfo[]>([]);
  useEffect(() => { void listAssets().then(setLibrary).catch(() => setLibrary([])); }, []);
  const toggle = (id: string) => setPictures((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= HANDOUT_LIMITS.pictures ? cur : [...cur, id]));
  const save = () => act({ kind: 'handout.set', handout: { id: start?.id ?? newId(), title: title.trim(), body, pictures } }, (r) => { if (r.ok) { setNotice(start ? 'Handout saved.' : 'Handout written. It is yours alone until you send it.'); onDone(); } });
  return (
    <form className="space-y-2 rounded border border-stone-700 p-2" aria-label={start ? 'Edit handout' : 'New handout'} onSubmit={(e) => { e.preventDefault(); save(); }}>
      <label className="block text-xs">Title<input className={input} value={title} maxLength={HANDOUT_LIMITS.title} onChange={(e) => setTitle(e.target.value)} /></label>
      <label className="block text-xs">Text<textarea className={`${input} h-32`} value={body} maxLength={HANDOUT_LIMITS.body} onChange={(e) => setBody(e.target.value)} /></label>
      <fieldset className="text-xs">
        <legend>Pictures from your Library (up to {HANDOUT_LIMITS.pictures})</legend>
        {library.length === 0 && <p className="text-stone-400">No pictures in your Library yet.</p>}
        <div className="flex max-h-32 flex-col overflow-y-auto">
          {library.map((a) => (
            <label key={a.id} className="flex items-center gap-1">
              <input type="checkbox" checked={pictures.includes(a.id)} onChange={() => toggle(a.id)} disabled={!pictures.includes(a.id) && pictures.length >= HANDOUT_LIMITS.pictures} />{a.name}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex gap-1">
        <button type="submit" className={btn} disabled={!title.trim()}>{start ? 'Save handout' : 'Write handout'}</button>
        <button type="button" className={btn} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function SendForm({ handout, onDone }: { handout: Handout; onDone: () => void }) {
  const { act, setNotice, store } = useTableState();
  const { table } = useShell();
  const players = table.members.filter((m) => m.role === 'player');
  const [chosen, setChosen] = useState<string[]>([]);
  const send = (to: 'all' | string[]) => act({ kind: 'handout.send', id: handout.id, to }, (r) => { if (r.ok) { setNotice(`Sent "${handout.title}".`); onDone(); } });
  return (
    <div className="space-y-1 rounded border border-stone-700 p-2 text-xs" role="group" aria-label={`Send ${handout.title}`}>
      <button type="button" className={btn} onClick={() => send('all')}>Send to everyone</button>
      {players.map((p) => (
        <label key={p.id} className="flex items-center gap-1">
          <input type="checkbox" checked={chosen.includes(p.id)} onChange={() => setChosen((c) => (c.includes(p.id) ? c.filter((x) => x !== p.id) : [...c, p.id]))} />
          {memberLabel(p, store.state.objects as never, {})}
        </label>
      ))}
      <div className="flex gap-1">
        <button type="button" className={btn} disabled={!chosen.length} onClick={() => send(chosen)}>Send to chosen</button>
        <button type="button" className={btn} onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

function NotePinForm({ onDone }: { onDone: () => void }) {
  const { act, setNotice } = useTableState();
  const [note, setNote] = useState('');
  const [label, setLabel] = useState('');
  const pin = () => {
    const at = boardBus.viewCentre() ?? { x: 0, y: 0 };
    act({ kind: 'pin.set', pin: { id: newId(), at, label: label.trim(), target: { note }, to: null } }, (r) => { if (r.ok) { setNotice('Note pinned at the centre of your view; drag it to move it. Players never see it.'); onDone(); } });
  };
  return (
    <form className="space-y-1 rounded border border-stone-700 p-2 text-xs" aria-label="Pin a note" onSubmit={(e) => { e.preventDefault(); pin(); }}>
      <label className="block">Label<input className={input} value={label} maxLength={HANDOUT_LIMITS.label} onChange={(e) => setLabel(e.target.value)} /></label>
      <label className="block">Note (yours alone)<textarea className={`${input} h-20`} value={note} maxLength={HANDOUT_LIMITS.note} onChange={(e) => setNote(e.target.value)} /></label>
      <div className="flex gap-1">
        <button type="submit" className={btn} disabled={!note.trim()}>Pin note</button>
        <button type="button" className={btn} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

type Mode = { kind: 'list' } | { kind: 'write'; handout: Handout | null } | { kind: 'send'; handout: Handout } | { kind: 'note' } | { kind: 'read'; id: string };

export function HandoutsPanel() {
  const { role, act, setNotice, handouts, store } = useTableState();
  const { table } = useShell();
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const mayAct = useMayAct();
  // The DM's controls are `notes`'s (issues 1159, 1160): a co-DM holding it is sent the DM's handouts whole, whichever
  // board they see; a guest DM without it reads them as a player. A note pin is seen on the DM's board alone, so it is
  // offered only there (a co-DM without `see_hidden` would pin a note they could not see).
  const dm = mayAct('handout.set');
  const notePins = dm && role === 'dm';
  const list = handoutList(handouts, dm ? 'dm' : role);
  const back = () => setMode({ kind: 'list' });
  const nameOf = (id: string) => memberLabel(table.members.find((m) => m.id === id), store.state.objects as never, {});
  const pins = Object.values((store.state.pins ?? {}) as Record<string, Pin>);

  if (mode.kind === 'write') return <div className="p-2 text-sm"><HandoutForm start={mode.handout} onDone={back} /></div>;
  if (mode.kind === 'send') return <div className="p-2 text-sm"><SendForm handout={mode.handout} onDone={back} /></div>;
  if (mode.kind === 'note') return <div className="p-2 text-sm"><NotePinForm onDone={back} /></div>;
  if (mode.kind === 'read') {
    const h = handouts[mode.id];
    return (
      <div className="space-y-2 p-2 text-sm">
        <button type="button" className={btn} onClick={back}>Back to handouts</button>
        {h ? <HandoutView handout={h} /> : <p>That handout is no longer yours to read.</p>}
      </div>
    );
  }

  const pinOn = (h: Handout) => {
    const at = boardBus.viewCentre() ?? { x: 0, y: 0 };
    act({ kind: 'pin.set', pin: { id: newId(), at, label: h.title.slice(0, HANDOUT_LIMITS.label), target: { handout: h.id }, to: null } }, (r) => { if (r.ok) setNotice(`"${h.title}" pinned at the centre of your view; drag it to move it. Players it was sent to see the pin.`); });
  };

  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-2 text-sm" data-testid="handouts">
      {dm && (
        <div className="flex flex-wrap gap-1">
          <button type="button" className={btn} onClick={() => setMode({ kind: 'write', handout: null })}>New handout</button>
          {notePins && <button type="button" className={btn} onClick={() => setMode({ kind: 'note' })}>Pin a note</button>}
        </div>
      )}
      {list.length === 0 && <p className="text-stone-400">{dm ? 'No handouts yet. Write one, then send it when the moment comes.' : 'No handouts yet. The DM sends them during play.'}</p>}
      <ul className="space-y-2">
        {list.map((h) => (
          <li key={h.id} className="rounded border border-stone-800 p-2" data-testid="handout-row">
            <button type="button" className="text-left font-semibold hover:underline" onClick={() => setMode({ kind: 'read', id: h.id })}>{h.title}</button>
            {dm && 'to' in h && (
              <>
                <p className="text-xs text-stone-400">{audienceWords(h, nameOf)}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <button type="button" className={btn} onClick={() => setMode({ kind: 'send', handout: h })}>Send to…</button>
                  {h.to !== null && <button type="button" className={btn} onClick={() => act({ kind: 'handout.take', id: h.id, from: 'all' }, (r) => { if (r.ok) setNotice(`Took back "${h.title}".`); })}>Take back</button>}
                  <button type="button" className={btn} onClick={() => setMode({ kind: 'write', handout: h })}>Edit</button>
                  <button type="button" className={btn} onClick={() => pinOn(h)}>Pin on the map</button>
                  <button type="button" className={btn} onClick={() => act({ kind: 'handout.delete', id: h.id }, (r) => { if (r.ok) setNotice(`Deleted "${h.title}".`); })}>Delete</button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      {dm && pins.length > 0 && (
        <section aria-label="Pins on this map" className="space-y-1">
          <h3 className="vtt-section-head">Pins on this map</h3>
          {pins.map((p) => (
            // A handout pin follows its handout: taken back (or never sent), its dot is gone from every player's map, and the list says so.
            <div key={p.id} className="flex items-center gap-1 text-xs" data-testid="pin-row">
              <span className="flex-1 truncate">{p.label || ('note' in p.target ? 'Note' : handouts[p.target.handout]?.title ?? 'Handout')}{'note' in p.target ? ' (note, yours alone)' : p.to === null || (Array.isArray(p.to) && p.to.length === 0) ? ' (no player sees it)' : ''}</span>
              <button type="button" className={btn} onClick={() => boardBus.goTo(p.at)}>Go to</button>
              <button type="button" className={btn} onClick={() => act({ kind: 'pin.remove', id: p.id })}>Remove pin</button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
